import React from 'react';
import Loader from 'relements/build/esm/decorators/loader';
import connection from '@src/connection';
import Tabs from 'relements/build/esm/ui/tabs';
import Text from 'relements/build/esm/input/text';

import { LANGUAGE_CODE_TO_TITLE } from '@src/constants/Language';
import { KEY_CODES } from '@src/constants/MogamboConstants';
import { getCRFData } from '@src/api/entities';
import UserResponse from '@src/components/UserResponseSynonymTable/UserResponse';
import SynonymContextMenu from '@src/components/UserResponseSynonymTable/SynonymContextMenu';

import * as maps from './maps';
import styles from './EntityPatterns.scss';

@connection(maps.listenToStores, maps.getStateFromStores)
@Loader({})
export default class EntityPatterns extends React.Component {
  static defaultProps = {
    crfData: {},
  };

  state = {
    inputText: '',
    responses: [],
    sentencesIndexed: {},
    currentLanguage: this.props.supportedLanguages[0],
  };

  componentDidMount() {
    this._fetchData(this.props.entityName, this.props.entityId);
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.entityName && nextProps.entityName) {
      this._fetchData(nextProps.entityName, nextProps.entityId);
    }
  }

  render() {
    const { sentencesIndexed, currentLanguage } = this.state;
    const responses = sentencesIndexed[currentLanguage] || [];
    const RENDER_TABS = this._getTabs();
    return (
      <div
        className={`undefined-text-entity-config ${styles.container}`}
        ref={(wrapper) => {
          this._wrapper = wrapper;
        }}
      >
        <div className={styles.containerColumn}>
          <div className={styles.tabs}>
            <Tabs
              className={styles.tabsItems}
              value={this.state.currentLanguage}
            >
              {RENDER_TABS.map((tab) => (
                <Tabs.Item
                  value={tab.path}
                  onClick={this._handleLanguageChange}
                >
                  {tab.title}
                </Tabs.Item>
              ))}
            </Tabs>
          </div>

          <div className={styles.header}>
            <Text
              label="Add a sentence to the pattern"
              value={this.state.inputText}
              onChange={(inputText) => this.setState({ inputText })}
              onKeyDown={this._handleKeyDown}
              className={styles.headerInput}
              placeholder="Start typing..."
              disabled={!this.props.canEdit}
            />
            <div className={styles.inputHint}>⏎ Press Enter</div>
            <div className={styles.bodyLabel}>
              Patterns to detect this entity
            </div>
          </div>
          <div className={styles.body}>
            {responses.map((response, i) => {
              return (
                <div
                  key={`${this.state.currentLanguage}-${i}`}
                  className={styles.bodyRow}
                >
                  <div className={styles.bodyRowCount}>{i + 1}</div>
                  <UserResponse
                    index={i}
                    userResponse={response}
                    domainTitle=""
                    onAddTag={this._addTag(i)}
                    onRemoveTag={this._removeTag(i)}
                    onEdit={this._editResponse(i)}
                    onToggle={this._toggleResponse(i)}
                    focusContextMenu={this._focusContextMenu}
                    blurContextMenu={this._blurContextMenu}
                    getWrapperRect={this._getWrapperRect}
                    synonyms={[]}
                    editedData={[]}
                    hoveringTooltips
                    typingSuggestionsDisabled
                    supportsTags
                  />
                </div>
              );
            })}
            {this.state.responses.length === 0 ? (
              <div className={styles.zeroState}>
                <div className={styles.zeroStateText}>
                  You haven't added any sentences yet
                </div>
              </div>
            ) : null}
          </div>
          <SynonymContextMenu
            offset={{ x: 24, y: 32 }}
            ref={(contextMenu) => {
              this._contextMenu = contextMenu;
            }}
          />
        </div>
        <div className={styles.containerColumn}>
          <div className={styles.containerQuestion}>How to use Patterns?</div>
          <div className={styles.containerAnswer}>
            Add a sentence and <em>highlight</em> a word/phrase (by right
            clicking) which should be detected as an entity. (Add minumum 10
            variations to make this work)
          </div>
          <div className={styles.containerQuestion}>When to use Patterns?</div>
          <div className={styles.containerAnswer}>
            When your value - variants dictionary can be infinite. Cases such as
            human names, where it is not possible to enter every name possible.
            Rather it makes sense to use example driven learning, where you
            teach the bot to look for some patterns to detect entities.
            <br />
            <br />
            Eg:
            <br />
            <br />
            You can call me Chirag
            <br />
            My name is Vaibhav
            <br />
            My full name is Jatin Parekh
            <br />
            <br />
            Highlight Chirag, Vaibhav and Jatin Parekh in the above sentences.
            This way the bot will learn patterns like "You can call me
            &lt;name&gt;", "My name is &lt;name&gt;" and so on and try to
            generalize.
            <br />
            <br />
            NOTE: Although this method works in conjunction with dictionary
            lookup, the detected parts of the user's message will only be
            resolved to some value if itis a variant in the entity's dictionary.
            For example for an airport_code entity, in text like 'I want to fly
            to new delhi', if the bot detects 'new delhi' but there is no entry
            for 'new delhi' in the dictionary to get its airport code', NER will
            return 'new delhi' instead of its airport code 'DEL'.
          </div>
        </div>
      </div>
    );
  }

  _setState = (state) => {
    this.setState(state, () => {
      const returnData = {};
      const { sentencesIndexed } = this.state;
      Object.keys(sentencesIndexed).map((languageCode) => {
        sentencesIndexed[languageCode].forEach(({ title, tags, active }) => {
          if (!returnData[languageCode]) returnData[languageCode] = [];
          if (!active) return;
          returnData[languageCode].push({
            sentence: title,
            entities: tags,
          });
        });
      });

      this.props.onChange({
        sentences: returnData,
        domain_name: this.props.domain.title,
      });
    });
  };

  _focusContextMenu = (
    contextMenuOptions,
    contextMenuPosition,
    wrapperRect
  ) => {
    this._contextMenu.focus(
      contextMenuOptions,
      {
        top: contextMenuPosition.top - 32,
        left: contextMenuPosition.left - 16,
      },
      wrapperRect
    );
  };

  _blurContextMenu = () => {
    if (this._contextMenu) this._contextMenu.blur();
  };

  _getWrapperRect = () => {
    return this._wrapper.getBoundingClientRect();
  };

  _fetchData = async (entityName, entityId) => {
    this.props.activateLoader();
    getCRFData(entityName, entityId)
      .then((response) => {
        this.props.deactivateLoader();
        const crfData = response.body;
        this._setResponses(crfData);
      })
      .catch((error) => {
        console.error(error);
        this.props.deactivateLoader();
      });
  };

  _setResponses = (dataIndexed) => {
    const sentencesIndexed = {};
    Object.keys(dataIndexed).map((languageCode) => {
      const sentences = dataIndexed[languageCode];
      sentences.forEach(({ sentence, entities }) => {
        if (!sentencesIndexed[languageCode])
          sentencesIndexed[languageCode] = [];
        sentencesIndexed[languageCode].push({
          title: sentence,
          active: true,
          tags: entities,
        });
      });
    });

    this.setState({
      sentencesIndexed,
      currentLanguage: this.props.supportedLanguages[0],
    });
  };

  _addResponse = () => {
    const { sentencesIndexed, currentLanguage } = this.state;
    if (!sentencesIndexed[currentLanguage])
      sentencesIndexed[currentLanguage] = [];
    const newResponses = sentencesIndexed[currentLanguage].concat([
      { title: this.state.inputText, active: true, tags: [] },
    ]);

    sentencesIndexed[currentLanguage] = newResponses;

    this._setState({ sentencesIndexed });
  };

  _editResponse = (index) => (response, value) => {
    const { sentencesIndexed, currentLanguage } = this.state;
    const responses = [...sentencesIndexed[currentLanguage]];
    responses[index].title = value;
    this._setState({ sentencesIndexed });
  };

  _toggleResponse = (index) => () => {
    const { sentencesIndexed, currentLanguage } = this.state;
    const responses = [...sentencesIndexed[currentLanguage]];
    responses[index].active = !responses[index].active;
    this._setState({ sentencesIndexed });
  };

  _addTag = (index) => (tag) => {
    const { sentencesIndexed, currentLanguage } = this.state;
    const responses = [...sentencesIndexed[currentLanguage]];
    responses[index].tags = responses[index].tags.concat([tag]);
    this._setState({ sentencesIndexed });
  };

  _removeTag = (index) => (tagToRemove) => {
    const { sentencesIndexed, currentLanguage } = this.state;
    const responses = [...sentencesIndexed[currentLanguage]];
    responses[index].tags = responses[index].tags.filter(
      (tag) => tag !== tagToRemove
    );
    this._setState({ sentencesIndexed });
  };

  _handleKeyDown = (e) => {
    if (e.keyCode === KEY_CODES.ENTER_KEY && this.state.inputText.length > 0) {
      this._addResponse();
      this.setState({ inputText: '' });
    }
  };

  validate = () => {
    return true;
  };

  _getTabs = () => {
    const enabledLanguages = this.props.enabledLanguages || [];
    const { supportedLanguages = [] } = this.props;
    return [...supportedLanguages]
      .sort((a, b) => {
        return enabledLanguages.indexOf(b) - enabledLanguages.indexOf(a);
      })
      .map((languageCode) => {
        return {
          path: languageCode,
          title: LANGUAGE_CODE_TO_TITLE[languageCode],
        };
      });
  };

  _handleLanguageChange = (language) => {
    this.setState({ currentLanguage: language });
  };
}
